import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  CloudUpload,
  Download,
  FileText,
  Loader2,
  X,
} from "lucide-react";
import axios from "axios";
import Papa from "papaparse";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ImportPreviewTable } from "./ImportPreviewTable";
import { importCompanies, validateImport } from "@/api/companies";
import type {
  ImportResponse,
  ImportValidateResponse,
} from "@/api/types";

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const TEMPLATE_URL = "/prism_import_template.csv";

const ACCEPTED_COLUMNS = [
  "name (required)",
  "domain",
  "sector",
  "headcount",
  "city",
  "country",
  "contact_name",
  "contact_email",
  "contact_title",
  "contact_linkedin",
  "signed_date",
  "service",
  "notes",
];

interface ImportClientsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectSlug: string;
  onImported: () => void;
}

type Phase =
  | { kind: "idle"; error?: string }
  | { kind: "validating" }
  | {
      kind: "preview";
      validation: ImportValidateResponse;
      error?: string;
    }
  | { kind: "importing" }
  | { kind: "done"; result: ImportResponse };

export function ImportClientsDialog({
  open,
  onOpenChange,
  projectSlug,
  onImported,
}: ImportClientsDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [dragOver, setDragOver] = useState(false);
  const [estimatedRows, setEstimatedRows] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Estimate row count locally for a pre-validation hint.
  useEffect(() => {
    if (!file) {
      setEstimatedRows(null);
      return;
    }
    let cancelled = false;
    let count = 0;
    Papa.parse<unknown>(file, {
      header: true,
      skipEmptyLines: true,
      step: () => {
        count++;
      },
      complete: () => {
        if (!cancelled) setEstimatedRows(count);
      },
      error: () => {
        if (!cancelled) setEstimatedRows(null);
      },
    });
    return () => {
      cancelled = true;
    };
  }, [file]);

  function reset() {
    setFile(null);
    setPhase({ kind: "idle" });
    setDragOver(false);
    setEstimatedRows(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleClose(next: boolean) {
    if (
      !next &&
      (phase.kind === "validating" || phase.kind === "importing")
    ) {
      return;
    }
    if (!next && phase.kind === "done") {
      onImported();
    }
    if (!next) reset();
    onOpenChange(next);
  }

  function pickFile(picked: File | undefined | null) {
    if (!picked) return;
    if (!picked.name.toLowerCase().endsWith(".csv")) {
      toast.error("Only .csv files are accepted.");
      return;
    }
    if (picked.size > MAX_FILE_BYTES) {
      toast.error("File is too large (5MB max).");
      return;
    }
    setFile(picked);
    setPhase({ kind: "idle" });
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files?.[0]);
  }

  async function handleValidate() {
    if (!file) return;
    setPhase({ kind: "validating" });
    try {
      const validation = await validateImport(projectSlug, file);
      setPhase({ kind: "preview", validation });
    } catch (err) {
      setPhase({ kind: "idle", error: extractErrorMessage(err) });
    }
  }

  async function handleImport() {
    if (phase.kind !== "preview" || !file) return;
    const previousValidation = phase.validation;
    setPhase({ kind: "importing" });
    try {
      const result = await importCompanies(projectSlug, file);
      setPhase({ kind: "done", result });
    } catch (err) {
      setPhase({
        kind: "preview",
        validation: previousValidation,
        error: extractErrorMessage(err),
      });
    }
  }

  function changeFile() {
    setFile(null);
    setPhase({ kind: "idle" });
    setEstimatedRows(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[90vh] max-w-[864px] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Import existing clients</DialogTitle>
          <DialogDescription>
            Upload a CSV with your signed clients. Companies are matched by
            domain to avoid duplicates.
          </DialogDescription>
        </DialogHeader>

        <div className="-mx-1 flex min-h-0 flex-1 flex-col overflow-y-auto px-1 py-1">
        {/* IDLE: drop zone or file chip */}
        {phase.kind === "idle" && (
          <div className="space-y-4">
            <a
              href={TEMPLATE_URL}
              download
              className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500"
            >
              <Download className="h-3.5 w-3.5" />
              Download CSV template
            </a>

            {file ? (
              <FileChip
                file={file}
                estimatedRows={estimatedRows}
                onRemove={changeFile}
              />
            ) : (
              <DropZone
                dragOver={dragOver}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
              />
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />

            <ColumnsHint />

            {phase.error && <InlineError message={phase.error} />}
          </div>
        )}

        {/* VALIDATING */}
        {phase.kind === "validating" && file && (
          <div className="space-y-4">
            <FileChip file={file} estimatedRows={estimatedRows} />
            <div className="flex items-center justify-center gap-2 rounded-xl border border-slate-900/[0.06] bg-slate-50/60 px-4 py-6 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
              Analysing file…
            </div>
          </div>
        )}

        {/* PREVIEW */}
        {phase.kind === "preview" && (
          <PreviewStep
            validation={phase.validation}
            error={phase.error}
            onChangeFile={changeFile}
          />
        )}

        {/* IMPORTING */}
        {phase.kind === "importing" && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-900/[0.06] bg-slate-50/60 px-4 py-6">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-700">
                  Import in progress…
                </span>
                <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full w-1/3 animate-pulse rounded-full bg-indigo-500" />
              </div>
            </div>
          </div>
        )}

        {/* DONE */}
        {phase.kind === "done" && <DoneStep result={phase.result} />}
        </div>

        {/* Footer */}
        <DialogFooter>
          {phase.kind === "idle" && (
            <>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleValidate}
                disabled={!file}
              variant="primary"
              >
                <CloudUpload className="mr-2 h-3.5 w-3.5" />
                Validate file
              </Button>
            </>
          )}

          {phase.kind === "validating" && (
            <Button variant="outline" disabled>
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              Validating…
            </Button>
          )}

          {phase.kind === "preview" && (
            <>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={phase.validation.valid === 0}
              variant="primary"
              >
                Import {phase.validation.valid} client
                {phase.validation.valid === 1 ? "" : "s"}
              </Button>
            </>
          )}

          {phase.kind === "importing" && (
            <Button variant="outline" disabled>
              Importing…
            </Button>
          )}

          {phase.kind === "done" && (
            <Button
              onClick={() => handleClose(false)}
              className="w-full"
            >
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DropZone({
  dragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onClick,
}: {
  dragOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        "flex h-[140px] w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed text-center transition-colors",
        dragOver
          ? "border-indigo-400 bg-indigo-50"
          : "border-slate-200 bg-slate-50/40 hover:border-slate-300 hover:bg-slate-50",
      )}
    >
      <CloudUpload
        className={cn(
          "h-8 w-8",
          dragOver ? "text-indigo-500" : "text-slate-400",
        )}
      />
      <div className="text-sm font-medium text-slate-700">
        Drop a CSV file here or click to browse
      </div>
      <div className="text-xs text-slate-500">.csv only · 5MB max</div>
    </button>
  );
}

function FileChip({
  file,
  estimatedRows,
  onRemove,
}: {
  file: File;
  estimatedRows: number | null;
  onRemove?: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white px-4 py-3 shadow-card-rest">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50">
        <FileText className="h-5 w-5 text-indigo-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-900">
          {file.name}
        </div>
        <div className="text-xs text-slate-500">
          {formatBytes(file.size)}
          {estimatedRows != null && (
            <>
              {" · "}~{estimatedRows} row{estimatedRows === 1 ? "" : "s"}
            </>
          )}
        </div>
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          aria-label="Remove file"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function ColumnsHint() {
  return (
    <div className="rounded-lg border border-slate-900/[0.06] bg-slate-50/50 p-3">
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-slate-500">
        Accepted columns
      </div>
      <div className="flex flex-wrap gap-1.5">
        {ACCEPTED_COLUMNS.map((c) => (
          <code
            key={c}
            className="rounded bg-white px-1.5 py-0.5 font-mono text-[11px] text-slate-600"
          >
            {c}
          </code>
        ))}
      </div>
    </div>
  );
}

function PreviewStep({
  validation,
  error,
  onChangeFile,
}: {
  validation: ImportValidateResponse;
  error?: string;
  onChangeFile: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-500">
          {validation.total_rows} row{validation.total_rows === 1 ? "" : "s"}
        </span>
        <SummaryBadge tone="emerald" label={`${validation.valid} valid`} />
        <SummaryBadge tone="rose" label={`${validation.invalid} invalid`} />
        <SummaryBadge
          tone="amber"
          label={`${validation.duplicates} duplicate${
            validation.duplicates === 1 ? "" : "s"
          }`}
        />
      </div>

      <ImportPreviewTable rows={validation.rows} />

      {validation.valid === 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50/60 p-4">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
          <div className="flex-1">
            <div className="text-sm font-medium text-rose-700">
              No valid rows to import
            </div>
            <div className="text-xs text-rose-600">
              Fix the file and try again.
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={onChangeFile}>
            Change file
          </Button>
        </div>
      )}

      {error && <InlineError message={error} />}
    </div>
  );
}

function DoneStep({ result }: { result: ImportResponse }) {
  const partial = result.skipped_invalid > 0 || result.errors.length > 0;

  return (
    <div className="space-y-3">
      <div
        className={cn(
          "rounded-xl border p-4",
          partial
            ? "border-amber-200 bg-amber-50/60"
            : "border-emerald-200 bg-emerald-50/60",
        )}
      >
        <div className="flex items-start gap-3">
          <CheckCircle2
            className={cn(
              "mt-0.5 h-5 w-5 shrink-0",
              partial ? "text-amber-600" : "text-emerald-600",
            )}
          />
          <div className="space-y-1">
            <div className="font-medium text-slate-900">
              {result.imported} client{result.imported === 1 ? "" : "s"}{" "}
              imported successfully
            </div>
            {result.skipped_duplicates > 0 && (
              <div className="text-sm text-slate-600">
                ⏭ {result.skipped_duplicates} duplicate
                {result.skipped_duplicates === 1 ? "" : "s"} skipped
              </div>
            )}
            {result.skipped_invalid > 0 && (
              <div className="text-sm text-slate-600">
                ❌ {result.skipped_invalid} invalid row
                {result.skipped_invalid === 1 ? "" : "s"} skipped
              </div>
            )}
          </div>
        </div>
      </div>

      {result.errors.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-rose-700">
            <AlertCircle className="h-4 w-4" />
            Errors
          </div>
          <ul className="max-h-32 space-y-1 overflow-y-auto pl-1 text-xs text-rose-700">
            {result.errors.map((e, i) => (
              <li key={i}>
                <span className="font-mono text-rose-500">row {e.row}</span> ·{" "}
                {e.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SummaryBadge({
  tone,
  label,
}: {
  tone: "emerald" | "rose" | "amber";
  label: string;
}) {
  const classes = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  }[tone];
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        classes,
      )}
    >
      {label}
    </span>
  );
}

function InlineError({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Something went wrong.";
}
